import { useCallback, useMemo, useRef, useState } from 'react';

import { MediaItem } from '../../../app/services/mediaItems';
import { quantizeToFrame } from '../../../utils/timecode';
import {
  applyOptionSelectionsToSegment,
  layoutSegmentClips,
  resolveSegmentDisplaySeconds,
  resolveSegmentVideo,
  resolveVideoSpans,
} from './linkMaSequence';
import { MaClip, MaOptionSlot, MaSegment, MaSequence, MaWarning, stripExtension } from './maTypes';
import {
  buildMaVariantGroups,
  createVariantResolver,
  MaVariantGroup,
} from './maVariantGroups';
import { serializeMaSequenceCsv } from './serializeMaCsv';

/**
 * タイムライン編集モードの状態管理フック。
 *
 * 現在表示中の MA セグメントのクリップをワーキングコピーとして保持し、
 * ナッジ / 削除 / 末尾トリム / ドロワーからの追加 / `_a`/`_b` バリアント切替 /
 * オプションスロット（`(x)` / `[x]` マーカー）の候補切替を行う。
 * 「更新」で CSV を再生成し、onSave（新テイクアップロード）へ渡す。
 *
 * - editedSegment はバリアント切替を適用した表示・再生用セグメント
 *   （クリップ変更のたびに layoutSegmentClips で後着優先カットを再計算）
 * - バリアント切替はシーケンス全体へ適用される（保存時に全行のファイル名を差し替え）
 * - オプション候補切替はスロット由来のクリップ一式（optionSlotKey 一致）を
 *   リンク済みテンプレート（MaOptionSlot.choices[].clips）で差し替える
 */

export interface MaTimelineEditApi {
  /** 編集モード中か */
  isEditing: boolean;
  /** 変更があるか（更新ボタンの活性制御） */
  isDirty: boolean;
  isSaving: boolean;
  selectedClipId: string | null;
  /** 編集対象のセグメント index（非編集時 null）。スライド差し替えの照合用 */
  editingSegmentIndex: number | null;
  /** 編集中セグメント（バリアント切替・レイアウト適用済み）。非編集時 null */
  editedSegment: MaSegment | null;
  /** ドロワーに並べるバリアントグループ（オーディオ + 比較ビデオ） */
  variantGroups: MaVariantGroup[];
  /** グループキー → 選択中 variation_name（未設定はグループ先頭 = _a） */
  variantOverrides: Record<string, string>;
  /** 編集中セグメントに属するオプションスロット（`(x)` / `[x]` マーカー） */
  optionSlots: MaOptionSlot[];
  /** スロットキー → 選択中候補キー（未設定は既定候補） */
  optionSelections: Record<string, string>;

  /**
   * 編集モード開始。initialOptionSelections（通常プレビューのチップで
   * 切り替えた選択）と initialSwitchOrder（切替の操作順。動画付きスロット
   * 競合時の後勝ち解決用）を渡すと、その状態を初期値として編集を始める
   */
  enterEdit: (
    _initialOptionSelections?: Record<string, string>,
    _initialSwitchOrder?: string[],
  ) => void;
  cancelEdit: () => void;
  selectClip: (_id: string | null) => void;
  /** 選択クリップを deltaSeconds ぶん移動（フレームに量子化、0 未満は 0） */
  nudgeSelected: (_deltaSeconds: number) => void;
  deleteSelected: () => void;
  /** クリップの表示尺を変更（末尾トリム）。フレーム量子化 + 実尺クランプ */
  trimClip: (_id: string, _durationSeconds: number) => void;
  /** ドロワーのグループをトラックへ追加 */
  addClip: (_group: MaVariantGroup, _startSeconds: number, _track: number) => void;
  /** `_a`/`_b` バリアント切替（シーケンス全体に適用） */
  switchVariant: (_groupKey: string, _variationName: string) => void;
  /** オプションスロットの候補切替（スロット由来のクリップ一式を差し替え） */
  selectOption: (_slotKey: string, _choiceKey: string) => void;
  /** CSV を再生成して保存（新テイク作成）。成功で編集モード終了 */
  save: () => Promise<void>;
}

let editIdCounter = 0;

/**
 * 編集で追加したクリップの擬似行番号ベース。
 * layoutSegmentClips の同時開始タイブレークは行番号昇順 = 後の行が「後着優先」のため、
 * 既存 CSV 行より大きい値にして追加クリップが優先されるようにする
 */
const EDIT_ADDED_LINE_BASE = 1_000_000;

export function useMaTimelineEdit(params: {
  sequence: MaSequence | null;
  /** 現在表示中の MA セグメント index（MA スライド以外は null） */
  segmentIndex: number | null;
  /** ショット内の全メディアアイテム（ドロワー・バリアント切替用。CSV は除外して渡す） */
  linkableItems: MediaItem[];
  /** CSV 再生成時のアップロード処理（Preview 側で新テイク作成） */
  onSave: (_csvText: string, _fileName: string) => Promise<void>;
  /** 保存 CSV のファイル名（アクティブが RPP の場合も呼び出し側で .csv に変換済み） */
  csvFileName: string | null;
}): MaTimelineEditApi {
  const { sequence, segmentIndex, linkableItems, onSave, csvFileName } = params;

  const [editing, setEditing] = useState<{ segmentIndex: number; clips: MaClip[] } | null>(null);
  const [selectedClipId, setSelectedClipId] = useState<string | null>(null);
  const [variantOverrides, setVariantOverrides] = useState<Record<string, string>>({});
  const [optionSelections, setOptionSelections] = useState<Record<string, string>>({});
  // 切替の操作順（末尾 = 最後に切り替えたスロット）。動画付きスロットの後勝ち解決用
  const [optionSwitchOrder, setOptionSwitchOrder] = useState<string[]>([]);
  const [isDirty, setIsDirty] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // 保存処理から最新値を参照するための ref
  const sequenceRef = useRef(sequence);
  sequenceRef.current = sequence;
  const editingRef = useRef(editing);
  editingRef.current = editing;

  const variantGroups = useMemo(
    () => buildMaVariantGroups(linkableItems),
    [linkableItems],
  );

  const fps = sequence?.fps ?? 30;

  // ===== 編集セッション =====

  const enterEdit = useCallback(
    (initialOptionSelections?: Record<string, string>, initialSwitchOrder?: string[]) => {
      const seq = sequenceRef.current;
      if (segmentIndex == null || !seq || !seq.segments[segmentIndex]) return;

      // 通常プレビューのチップで切り替えた選択があれば、その状態から編集を開始する
      // （既定候補のまま・不正キーは除外して正規化）
      const slots = seq.optionSlots.filter(slot => slot.segmentIndex === segmentIndex);
      const selections: Record<string, string> = {};
      slots.forEach(slot => {
        const key = initialOptionSelections?.[slot.key];
        if (key != null && key !== slot.defaultChoiceKey && slot.choices.some(c => c.key === key)) {
          selections[slot.key] = key;
        }
      });
      // 切替の操作順はこのセグメントのスロットに絞って引き継ぐ
      const switchOrder = (initialSwitchOrder ?? []).filter(key =>
        slots.some(slot => slot.key === key),
      );
      // 編集モードは「候補のテンプレート配置が正」（serializeMaCsv 参照）。
      // 可変尺分岐のシフトは適用せず、保存 CSV に本編直置きクリップの
      // シフト位置が焼き込まれないようにする（プレビュー / 書き出しでは適用される）
      const base = applyOptionSelectionsToSegment(
        seq.segments[segmentIndex],
        slots,
        selections,
        switchOrder,
        { variableLengthShift: false },
      );

      editIdCounter += 1;
      const sessionPrefix = `e${editIdCounter}`;
      const clips = base.clips.map((clip, i) => ({
        ...clip,
        editId: `${sessionPrefix}-${i}`,
      }));
      setEditing({ segmentIndex, clips });
      setSelectedClipId(null);
      setVariantOverrides({});
      setOptionSelections(selections);
      setOptionSwitchOrder(switchOrder);
      // プレビューで切り替え済みの選択は保存対象の変更として扱う（更新ボタンを活性化）
      setIsDirty(Object.keys(selections).length > 0);
    },
    [segmentIndex],
  );

  const cancelEdit = useCallback(() => {
    setEditing(null);
    setSelectedClipId(null);
    setVariantOverrides({});
    setOptionSelections({});
    setOptionSwitchOrder([]);
    setIsDirty(false);
  }, []);

  const selectClip = useCallback((id: string | null) => {
    setSelectedClipId(id);
  }, []);

  // ===== クリップ操作 =====

  const updateClips = useCallback(
    (updater: (_clips: MaClip[]) => MaClip[]) => {
      setEditing(prev => (prev ? { ...prev, clips: updater(prev.clips) } : prev));
      setIsDirty(true);
    },
    [],
  );

  /**
   * オプションスロット由来のクリップは編集不可。
   * 候補のテンプレート配置が正（保存 CSV に行として出力されず、トリム等の
   * 個別編集が保存されない）ため、hook の入口で一律に弾く。
   * 音を変えたい場合は候補切替（selectOption）か候補側（パレット）の修正で行う
   */
  const isSlotOwned = useCallback(
    (id: string | null) =>
      id != null && !!editingRef.current?.clips.some(c => c.editId === id && c.optionSlotKey),
    [],
  );

  const nudgeSelected = useCallback(
    (deltaSeconds: number) => {
      if (!selectedClipId || isSlotOwned(selectedClipId)) return;
      updateClips(clips =>
        clips.map(clip =>
          clip.editId === selectedClipId
            ? {
                ...clip,
                startSeconds: quantizeToFrame(
                  Math.max(0, clip.startSeconds + deltaSeconds),
                  fps,
                ),
              }
            : clip,
        ),
      );
    },
    [selectedClipId, isSlotOwned, updateClips, fps],
  );

  const deleteSelected = useCallback(() => {
    if (!selectedClipId || isSlotOwned(selectedClipId)) return;
    updateClips(clips => clips.filter(clip => clip.editId !== selectedClipId));
    setSelectedClipId(null);
  }, [selectedClipId, isSlotOwned, updateClips]);

  const trimClip = useCallback(
    (id: string, durationSeconds: number) => {
      if (isSlotOwned(id)) return;
      updateClips(clips =>
        clips.map(clip => {
          if (clip.editId !== id) return clip;
          const intrinsic =
            clip.audioItem.duration != null && clip.audioItem.duration > 0
              ? clip.audioItem.duration
              : Infinity;
          const clamped = Math.min(
            Math.max(1 / fps, quantizeToFrame(durationSeconds, fps)),
            intrinsic,
          );
          return { ...clip, durationSeconds: clamped, hasExplicitEnd: true };
        }),
      );
    },
    [isSlotOwned, updateClips, fps],
  );

  const addClip = useCallback(
    (group: MaVariantGroup, startSeconds: number, track: number) => {
      const item = group.options[0]?.latestTake;
      if (!item || item.file_type !== 'audio') return;
      editIdCounter += 1;
      const editId = `add-${editIdCounter}`;
      const clip: MaClip = {
        audioItem: item,
        sourceName: stripExtension(item.file_name),
        startSeconds: quantizeToFrame(Math.max(0, startSeconds), fps),
        durationSeconds: item.duration != null && item.duration > 0 ? item.duration : null,
        hasExplicitEnd: false,
        truncateAtSeconds: null,
        track: Math.max(1, Math.round(track)),
        trackName: null,
        gainDb: 0,
        // CSV 行なし。同時開始タイブレークで既存行より「後着」扱いにする
        line: EDIT_ADDED_LINE_BASE + editIdCounter,
        editId,
      };
      updateClips(clips => {
        // 名前付きレーン（RPP のトラック名等）へ追加した場合は名前を引き継ぐ
        // （CSV 再生成時に同じレーンへラウンドトリップさせるため）
        const laneName =
          clips.find(c => c.track === clip.track && c.trackName != null)?.trackName ??
          sequenceRef.current?.segments
            .flatMap(s => s.clips)
            .find(c => c.track === clip.track && c.trackName != null)?.trackName ??
          null;
        return [...clips, { ...clip, trackName: laneName }];
      });
      setSelectedClipId(editId);
    },
    [updateClips, fps],
  );

  const switchVariant = useCallback((groupKey: string, variationName: string) => {
    setVariantOverrides(prev => ({ ...prev, [groupKey]: variationName }));
    setIsDirty(true);
  }, []);

  // ===== オプションスロット（(x) / [x] マーカー）の候補切替 =====

  /** 編集中セグメントに属するスロット（ドロワーの切替 UI 用） */
  const optionSlots = useMemo((): MaOptionSlot[] => {
    if (!editing || !sequence) return [];
    return sequence.optionSlots.filter(slot => slot.segmentIndex === editing.segmentIndex);
  }, [editing, sequence]);

  const selectOption = useCallback(
    (slotKey: string, choiceKey: string) => {
      const seq = sequenceRef.current;
      const slot = seq?.optionSlots.find(s => s.key === slotKey);
      const choice = slot?.choices.find(c => c.key === choiceKey);
      if (!slot || !choice) return;
      // 差し替えで消えるクリップが選択中なら選択を解除
      if (editing?.clips.some(c => c.optionSlotKey === slotKey && c.editId === selectedClipId)) {
        setSelectedClipId(null);
      }
      editIdCounter += 1;
      const sessionPrefix = `opt${editIdCounter}`;
      updateClips(clips => [
        ...clips.filter(c => c.optionSlotKey !== slotKey),
        ...choice.clips.map((clip, i) => ({ ...clip, editId: `${sessionPrefix}-${i}` })),
      ]);
      setOptionSelections(prev => ({ ...prev, [slotKey]: choiceKey }));
      // 操作順の末尾へ（動画付きスロット競合時は最後に切り替えたスロットが勝つ）
      setOptionSwitchOrder(prev => [...prev.filter(key => key !== slotKey), slotKey]);
    },
    [editing, selectedClipId, updateClips],
  );

  // ===== 編集中セグメントの構築（表示・再生用） =====

  const editedSegment = useMemo((): MaSegment | null => {
    if (!editing || !sequence) return null;
    const base = sequence.segments[editing.segmentIndex];
    if (!base) return null;

    const resolve = createVariantResolver(variantGroups, variantOverrides);
    // 編集中の警告（同時開始カット等）は UI 未表示のため破棄する
    const warnings: MaWarning[] = [];
    const clips = editing.clips.map(clip => ({
      ...clip,
      audioItem: resolve(clip.audioItem),
    }));
    const clipsEndSeconds = layoutSegmentClips(clips, warnings);

    // 表示動画: 本編は `_a/_b` バリアント切替を適用し（差し替え時は表示尺を
    // 差し替え先の duration から再計算。end 明示時は維持）、オプション候補の
    // 動画はスロット位置からの区間（videoSpans）として解決する
    const slotsOfSegment = sequence.optionSlots.filter(
      slot => slot.segmentIndex === editing.segmentIndex,
    );
    const baseVideoItem = resolve(base.baseVideoItem);
    const { videoDisplaySeconds } = resolveSegmentVideo(base, baseVideoItem);
    const videoSpans = resolveVideoSpans({
      baseSpans: base.baseVideoSpans,
      slots: slotsOfSegment,
      selections: optionSelections,
      switchOrder: optionSwitchOrder,
      resolveItem: resolve,
      baseDisplaySeconds: videoDisplaySeconds,
    });

    return {
      ...base,
      videoItem: videoSpans[0].videoItem,
      videoSpans,
      videoDisplaySeconds,
      // 本編より長い候補動画が選択中なら表示尺を候補終端まで延長
      resolvedDisplaySeconds: resolveSegmentDisplaySeconds(videoDisplaySeconds, videoSpans),
      clips,
      clipsEndSeconds,
    };
  }, [editing, sequence, variantGroups, variantOverrides, optionSelections, optionSwitchOrder]);

  // ===== 保存（CSV 再生成 → 新テイクアップロード） =====

  const save = useCallback(async () => {
    const seq = sequenceRef.current;
    if (!editing || !seq || !csvFileName || isSaving) return;

    const resolve = createVariantResolver(variantGroups, variantOverrides);
    // 編集セグメントのクリップを差し替えた保存用シーケンスを構築
    // （バリアント差し替えはシリアライザの resolve で全行に適用。
    //   オプションスロットは構造ごと書き出され、optionSelections が
    //   `(名前=候補)` として記録されるため保存後も切替可能なまま）
    const editedClips = [...editing.clips].sort(
      (a, b) => a.startSeconds - b.startSeconds || a.line - b.line,
    );
    const segments = seq.segments.map((seg, i) =>
      i === editing.segmentIndex ? { ...seg, clips: editedClips } : seg,
    );
    const csvText = serializeMaSequenceCsv(
      { ...seq, segments },
      { resolveItem: resolve, optionSelections },
    );

    setIsSaving(true);
    try {
      await onSave(csvText, csvFileName);
      // 成功: 編集モード終了（refetch で新テイクのシーケンスに切り替わる）
      cancelEdit();
    } finally {
      setIsSaving(false);
    }
  }, [
    editing,
    csvFileName,
    isSaving,
    variantGroups,
    variantOverrides,
    optionSelections,
    onSave,
    cancelEdit,
  ]);

  return {
    isEditing: !!editing,
    isDirty,
    isSaving,
    selectedClipId,
    editingSegmentIndex: editing?.segmentIndex ?? null,
    editedSegment,
    variantGroups,
    variantOverrides,
    optionSlots,
    optionSelections,
    enterEdit,
    cancelEdit,
    selectClip,
    nudgeSelected,
    deleteSelected,
    trimClip,
    addClip,
    switchVariant,
    selectOption,
    save,
  };
}
